import CantileverTheme from 'styles/cantileverTheme'

const palette = {
  ...CantileverTheme.colors,
  grayscale: {
    ...CantileverTheme.colors.grayscale,
    '650': 'rgba(34,34,34,1)',
  },
}

export type ForecastTheme = {
  pageBackground: string
  background1: string
  background2: string
  background2_5: string
  background3: string
  background4: string
  icon1: string
  icon2: string
  border: string
  border2: string
  textDisabled: string

  navSidebarBackground?: string
  pageBackground1?: string
  cardBackground?: string
  cardGroupBackground?: string
  cardDividerBackground?: string
  glassBackground?: string
  greenGlassBackground?: string
  redGlassBackground?: string
  blueGlassBackground?: string
  modalBackground?: string
  activeText: string
  defaultText: string
  secondaryText: string
  tertiaryText: string
  defaultButton?: string
  hoverButton?: string
  activeButton?: string
  defaultGhostButton?: string
  hoverGhostButton?: string
  activeGhostButton?: string
  boxShadow?: string

  danger?: string
  dangerText?: string
  primary?: string
  warning?: string
  highlight?: string
}

export const ForecastDarkTheme: ForecastTheme = {
  pageBackground: palette.grayscale[600],
  background1: palette.grayscale[800],
  background2: palette.grayscale[700],
  background2_5: palette.grayscale[650],
  background3: palette.grayscale[600],
  background4: palette.grayscale[500],
  icon1: palette.grayscale[100],
  icon2: palette.grayscale[200],
  border: palette.grayscale[300],
  border2: palette.grayscale[400],
  textDisabled: palette.grayscale[400],
  navSidebarBackground: 'rgba(0, 0, 0, 1)',
  pageBackground1: 'rgba(38, 38, 38, 1)',
  cardBackground: 'rgba(64, 64, 64, 1)',
  cardGroupBackground: 'rgba(0, 0, 0, .12)',
  cardDividerBackground: 'rgba(0, 0, 0, .25)',
  glassBackground: 'rgba(102,102,102,0.5)',
  greenGlassBackground: 'rgba(132, 204, 22, 0.1)',
  modalBackground: `#6c6c6c`,
  redGlassBackground: 'rgba(255, 0, 0, 0.3)',
  blueGlassBackground: 'rgba(0, 100, 255, 0.2)',
  defaultText: 'rgba(229, 229, 229, 1)',
  secondaryText: 'rgba(173, 173, 173, 1)',
  tertiaryText: 'rgba(123, 123, 123, 1)',
  activeText: 'rgba(250, 250, 250, 1)',
  defaultButton: 'rgba(255, 255, 255, .1)',
  hoverButton: 'rgba(255, 255, 255, .15)',
  activeButton: 'rgba(255, 255, 255, .2)',
  defaultGhostButton: 'rgba(255, 255, 255, 0)',
  hoverGhostButton: 'rgba(255, 255, 255, 0.08)',
  activeGhostButton: 'rgba(255, 255, 255, .15)',
  boxShadow: 'rgba(0, 0, 0, 0.1)',
  danger: '#F92F06',
  dangerText: '#FF8980',
  primary: '#007BFF',
  warning: '#BD8109',
  highlight: 'rgba(15, 143, 255, 0.65)', // Low opacity blue 400
}

const zinc = {
  zinc50: 'rgb(250, 250, 250)',
  zinc300: 'rgb(212, 212, 216)',
  zinc400: 'rgb(161, 161, 170)',
  zinc500: 'rgb(113, 113, 122)',
  zinc700: 'rgb(63, 63, 70)',
  zinc800: 'rgb(39, 39, 42)',
  zinc850: 'rgb(30, 30, 34)',
  zinc900: 'rgb(24, 24, 27)',
  zinc950: 'rgb(9, 9, 11)',
  borderSubtle: 'rgb(255 255 255 / 10%)',
}

export const ForecastZincTheme: ForecastTheme = {
  ...ForecastDarkTheme,
  pageBackground: zinc.zinc950,
  background1: zinc.zinc950,
  background2: zinc.zinc900,
  background2_5: zinc.zinc850,
  background3: zinc.zinc800,
  background4: zinc.zinc700,
  icon1: zinc.zinc50,
  icon2: zinc.zinc400,
  border: zinc.zinc700,
  border2: zinc.zinc700,
  textDisabled: zinc.zinc500,
  navSidebarBackground: zinc.zinc950,
  pageBackground1: zinc.zinc900,
  cardBackground: zinc.zinc900,
  cardGroupBackground: zinc.zinc800,
  cardDividerBackground: zinc.borderSubtle,
  glassBackground: 'rgb(39 39 42 / 50%)',
  modalBackground: zinc.zinc900,
  defaultText: zinc.zinc300,
  secondaryText: zinc.zinc400,
  tertiaryText: zinc.zinc500,
  activeText: zinc.zinc50,
  defaultButton: 'rgb(255 255 255 / 8%)',
  hoverButton: 'rgb(255 255 255 / 12%)',
  activeButton: 'rgb(255 255 255 / 16%)',
  defaultGhostButton: 'rgb(255 255 255 / 0%)',
  hoverGhostButton: 'rgb(255 255 255 / 8%)',
  activeGhostButton: 'rgb(255 255 255 / 12%)',
}

export const ForecastLightTheme: ForecastTheme = {
  pageBackground: palette.grayscale[300],
  background1: palette.grayscale[100],
  background2: palette.grayscale[200],
  background2_5: palette.grayscale[250],
  background3: palette.grayscale[300],
  background4: palette.grayscale[400],
  icon1: palette.grayscale[800],
  icon2: palette.grayscale[700],
  border: palette.grayscale[700],
  border2: palette.grayscale[300],
  textDisabled: palette.grayscale[500],
  navSidebarBackground: 'rgba(229, 229, 229, 1)',
  pageBackground1: 'rgba(242, 242, 242, 1)',
  cardBackground: 'rgba(255, 255, 255, 1)',
  cardGroupBackground: 'rgba(0, 0, 0, .03)',
  cardDividerBackground: 'rgba(0, 0, 0, .1)',
  glassBackground: 'rgba(255,255,255,0.5)',
  defaultText: 'rgba(38, 38, 38, 1)',
  secondaryText: 'rgba(82, 82, 82, 1)',
  tertiaryText: 'rgba(115, 115, 115, 1)',
  activeText: 'rgba(10, 10, 10, 1)',
  defaultButton: 'rgba(0, 0, 0, .07)',
  hoverButton: 'rgba(0, 0, 0, .12)',
  activeButton: 'rgba(0, 0, 0, .17)',
  defaultGhostButton: 'rgba(0, 0, 0, 0)',
  hoverGhostButton: 'rgba(0, 0, 0, 0.03)',
  activeGhostButton: 'rgba(0, 0, 0, .07)',
  greenGlassBackground: 'rgba(132, 204, 22, 0.1)',
  redGlassBackground: 'rgba(255, 0, 0, 0.3)',
  blueGlassBackground: 'rgba(0, 100, 255, 0.2)',
  boxShadow: 'rgba(0, 0, 0, 0.1)',
  danger: '#F92F06',
  dangerText: '#F92F06',
  primary: '#007BFF',
  warning: '#FAAD14',
  highlight: 'rgba(15, 143, 255, 0.65)', // Low opacity blue 400
}

const freedom = {
  red1: 'rgb(81, 28, 28)',
  red2: 'rgb(150, 53, 53)',
  red3: 'rgb(183, 65, 65)',
  red4: 'rgb(212, 79, 79)',
  blue1: 'rgb(47, 58, 130)',
  blue2: 'rgb(78, 88, 162)',
  blue3: 'rgb(105, 116, 187)',
  white1: 'rgb(255, 255, 255)',
  white2: 'rgb(217, 217, 217)',
  white3: 'rgb(236, 236, 236)',
  white4: 'rgb(179, 179, 179)',
}

export const ForecastFreedomTheme = {
  pageBackground: freedom.red1,
  background1: freedom.blue1,
  background2: freedom.red2,
  background2_5: freedom.blue2,
  background3: freedom.red3,
  background4: freedom.red4,
  activeText: freedom.white1,
  defaultText: freedom.white2,
  secondaryText: freedom.white3,
  tertiaryText: freedom.white4,
  icon1: freedom.white1,
  icon2: freedom.white2,
  border: freedom.white1,
  border2: freedom.white2,
  textDisabled: freedom.white2,
}

const barbie = {
  pink1: 'rgb(173, 62, 128)',
  pink2: 'rgb(195, 88, 151)',
  pink3: 'rgb(210, 113, 169)',
  pink4: 'rgb(232, 146, 198)',
  yellow1: 'rgb(244, 222, 163)',
  yellow2: 'rgb(222, 193, 115)',
  blue1: 'rgb(151, 204, 207)',
  blue2: 'rgb(124, 200, 204)',
}

export const ForecastBarbieTheme = {
  pageBackground: barbie.pink2,
  background1: barbie.pink1,
  background2: barbie.pink2,
  background2_5: barbie.pink3,
  background3: barbie.pink3,
  background4: barbie.pink4,
  activeText: barbie.blue1,
  defaultText: barbie.blue2,
  secondaryText: barbie.yellow1,
  tertiaryText: barbie.yellow2,
  icon1: barbie.blue1,
  icon2: barbie.yellow1,
  border: barbie.blue1,
  border2: barbie.yellow1,
  textDisabled: barbie.yellow2,
}

export const ForecastHotDogStandTheme = {
  pageBackground: 'rgb(125, 8, 6)',
  background1: 'rgb(209, 3, 0)',
  background2: 'rgb(171, 2, 0)',
  background2_5: 'rgb(150, 2, 0)',
  background3: 'rgb(125, 8, 6)',
  background4: 'rgb(100, 0, 0)',
  activeText: 'rgb(240, 182, 22)',
  defaultText: 'rgb(196, 148, 14)',
  secondaryText: 'rgb(171, 132, 22)',
  tertiaryText: 'rgb(149, 113, 15)',
  icon1: 'rgb(240, 182, 22)',
  icon2: 'rgb(196, 148, 14)',
  border: 'rgb(240, 182, 22)',
  border2: 'rgb(125, 8, 6)',
  textDisabled: 'rgb(171, 132, 22)',
}

const spooky = {
  orange1: 'rgb(255, 127, 36)',
  orange2: 'rgb(227, 110, 27)',
  orange3: 'rgb(184, 88, 20)',
  orange4: 'rgb(135, 64, 14)',

  gray500: 'rgb(105, 90, 79)',
  gray600: 'rgb(51, 46, 43)',
  gray650: 'rgb(36, 31, 28)',
  gray700: 'rgb(18, 14, 12)',
  gray800: 'rgb(10, 4, 1)',
}

export const ForecastSpookyTheme = {
  pageBackground: spooky.gray600,
  background1: spooky.gray800,
  background2: spooky.gray700,
  background2_5: spooky.gray650,
  background3: spooky.gray600,
  background4: spooky.gray500,
  activeText: spooky.orange1,
  defaultText: spooky.orange2,
  secondaryText: spooky.orange3,
  tertiaryText: spooky.orange4,
  icon1: spooky.orange1,
  icon2: spooky.orange2,
  border: spooky.orange1,
  border2: spooky.gray600,
  textDisabled: spooky.orange4,
}

const brat = {
  // should be (154, 205, 63) but forecast colors aren't rendering accurately
  // so this should match actual brat green ^
  green: 'rgb(139, 207, 2)',
  black: 'rgb(0, 0, 0)',
}

export const ForecastBratTheme = {
  pageBackground: brat.green,
  background1: brat.green,
  background2: brat.green,
  background2_5: brat.green,
  background3: brat.green,
  background4: brat.green,
  activeText: brat.black,
  defaultText: brat.black,
  secondaryText: brat.black,
  tertiaryText: brat.black,
  icon1: brat.black,
  icon2: brat.black,
  border: brat.black,
  border2: brat.black,
  textDisabled: brat.black,
}

const bratRemix = {
  greenDarker2: 'rgb(89, 133, 0)',
  greenDarker1: 'rgb(125, 190, 2)',
  actualBratGreen: 'rgb(139, 207, 2)',
  greenLighter1: 'rgb(154, 224, 13)',
  greenLighter2: 'rgb(181, 245, 54)',
  green4: 'rgb(89, 133, 0)',
  black1: 'rgb(0, 0, 0)',
  black2: 'rgb(20, 20, 20)',
  black3: 'rgb(40, 40, 40)',
  black4: 'rgb(60, 60, 60)',
}

export const ForecastBratRemixTheme = {
  pageBackground: bratRemix.actualBratGreen,
  background1: bratRemix.greenLighter2,
  background2: bratRemix.greenLighter1,
  background2_5: bratRemix.actualBratGreen,
  background3: bratRemix.greenDarker1,
  background4: bratRemix.greenDarker2,
  activeText: bratRemix.black1,
  defaultText: bratRemix.black2,
  secondaryText: bratRemix.black3,
  tertiaryText: bratRemix.black4,
  icon1: bratRemix.black1,
  icon2: bratRemix.black2,
  border: bratRemix.black1,
  border2: bratRemix.greenDarker1,
  textDisabled: bratRemix.black4,
}
